import React, {useEffect, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import Constants from "../../../utils/constants";
import {helper} from "../../../utils/helper";
import {AnomalieChip} from "./AnomaliesTable.js";
import ConsigneSnapshotRow from "../layout/ConsigneSnapshotRow.js";
import {describePrecedente, isRecidive} from "./recidiveRules.js";

const PHOTO_LABELS = {
    avant: 'Avant',
    apres: 'Après',
    problem: 'Problème',
	inaccessible: 'Lavage impossible',
};

const PhotoThumb = ({photo}) => {
    const url = `${Constants.API_ASSETS_PHOTO_INTERVENTION}/${photo.filepath}`;
    return (
        <Box sx={{position: 'relative'}}>
            <a href={url} target="_blank" rel="noreferrer">
                <img
                    src={url}
                    alt={photo.type}
                    style={{
                        width: 130,
                        height: 130,
                        objectFit: 'cover',
                        borderRadius: 8,
                        border: '1px solid #ddd'
                    }}
                />
            </a>
            <Chip
                size="small"
                label={PHOTO_LABELS[photo.type] ?? photo.type}
                sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 4,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    fontSize: '0.7rem'
                }}
            />
        </Box>
    );
};

const InfoRow = ({label, value}) => (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{fontWeight: 500, textAlign: 'right'}}>{value ?? '—'}</Typography>
    </Stack>
);

const problemeIri = (p) => p['@id'] ?? `/api/problems/${p.id}`;

/**
 * Panneau de traitement d'une anomalie, conditionnel au type :
 *
 * ECART_QUANTITE (existant) :
 * 1. "Le terrain a raison"   → aligne le patrimoine sur le nombre lavé
 * 2. "Corriger la saisie"    → corrige nombreLaves sur l'intervention
 * 3. "Ignorer"               → écart justifié, on ne touche à rien
 *
 * LAVAGE_IMPOSSIBLE (cf. doc/features/lavageImpossibleValidation.md) :
 * 1. "RAS — acquitter"           → saisie et problème cohérents
 * 2. "Enregistrer le problème"   → corrige le problème rattaché + acquitte
 * 3. "Requalifier en lavage"     → l'intervention repasse accessible (auto-résolution back)
 */
function AnomalieDetailDrawer({anomalie, open, onClose, onPatrimoineMaj, onCorriger, onIgnorer, onAcquitter, onModifierProbleme, problems, resolving}) {
    const [nombreLavesCorrige, setNombreLavesCorrige] = useState('');
    const [selectedProblemes, setSelectedProblemes] = useState([]);

    const isLavageImpossible = anomalie?.type === Constants.ANOMALIE_TYPE_LAVAGE_IMPOSSIBLE;

    useEffect(() => {
        // Pré-rempli avec la valeur attendue : dans 90% des cas de "gros doigts",
        // la bonne valeur est celle du patrimoine. Pour une requalification de
        // lavage impossible, la quantité du conteneur est aussi le bon défaut.
        setNombreLavesCorrige(anomalie?.quantitePatrimoine ?? anomalie?.intervention?.conteneur?.quantite ?? '');
        setSelectedProblemes(anomalie?.intervention?.problemes ?? []);
    }, [anomalie]);

    if (!anomalie) return null;

    const intervention = anomalie.intervention ?? {};
    const conteneur = intervention.conteneur ?? {};
    const consignes = anomalie.details?.consignes;
    const recidive = isRecidive(anomalie);

    // Lavage impossible : les photos d'inaccessibilité d'abord (élément décisif)
    const photos = [...(intervention.photos ?? [])].sort((a, b) => {
        if (!isLavageImpossible) return 0;
        return (b.type === 'inaccessible') - (a.type === 'inaccessible');
    });

    // Problèmes proposés : uniquement les critiques (importance 4 = raison d'un
    // lavage impossible ; en dessous = maintenance), de la typologie du conteneur
    // (+ génériques sans typologie).
    const problemesDisponibles = (problems ?? []).filter(p =>
        p.importance === Constants.PROBLEM_IMPORTANCE_CRITIQUE &&
        (!p.conteneurType || !conteneur.conteneurType || p.conteneurType === conteneur.conteneurType)
    );

    const problemesModifies =
        JSON.stringify((intervention.problemes ?? []).map(p => p.id).sort()) !==
        JSON.stringify(selectedProblemes.map(p => p.id).sort());

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{width: 480, maxWidth: '95vw', p: 3}} role="presentation">
                {/* --- Header --- */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                        {conteneur.nomenclature ?? `Intervention #${intervention.id}`}
                    </Typography>
                    <IconButton onClick={onClose} aria-label="Fermer">
                        <CloseIcon/>
                    </IconButton>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
                    <AnomalieChip anomalie={anomalie}/>
                </Stack>

                {/* --- Infos intervention --- */}
                <Stack spacing={0.75} sx={{mb: 2}}>
                    <InfoRow label="Date" value={helper.getFormattedDatetime(intervention.dateEvenement)}/>
                    <InfoRow label="Patrimoine" value={conteneur.patrimoine?.libelle}/>
                    <InfoRow label="Adresse"
                             value={[conteneur.adresse, conteneur.commune].filter(Boolean).join(', ')}/>
                    <InfoRow label="Opérateur" value={intervention.operateur?.libelle}/>
                    <InfoRow label="Typologie" value={intervention.typologieLavageLibelle}/>
                    {intervention.observation &&
                        <InfoRow label="Observation" value={intervention.observation}/>
                    }
                </Stack>

                {/* --- Récidive (CQ-01) : le passage précédent du conteneur était déjà impossible.
                     Information seule, calculée par l'API ; l'exploitant suit hors outil, la
                     résolution reste celle du lavage impossible ci-dessous. --- */}
                {recidive && (
                    <Box sx={{border: '1px solid #5b1a1a', backgroundColor: '#fbf4f4', borderRadius: 2, p: 1.5, mb: 2}}>
                        <Typography variant="subtitle2" sx={{color: '#5b1a1a', mb: 0.5}}>
                            Point récidiviste
                        </Typography>
                        <Typography variant="body2">
                            {describePrecedente(anomalie)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Deux passages impossibles de suite sur ce point (partiel compris). Le prochain lavage réussi n'a rien à fermer ici : cette ligne reste à traiter.
                        </Typography>
                    </Box>
                )}

                {/* --- Consignes du point valides le jour du passage (snapshot API) :
                     permet de distinguer « consigne non lue » de « lavage réellement impossible ».
                     `undefined` = anomalie antérieure au snapshot. --- */}
                {consignes && consignes?.length > 0 && (
                    <>
                        <Divider sx={{my: 2}}/>
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            Consignes du point au moment du passage{consignes ? ` (${consignes.length})` : ''}
                        </Typography>
	                    <Stack spacing={1}>
		                    {consignes?.map((c, i) => <ConsigneSnapshotRow key={c.id ?? i} consigne={c}/>)}
	                    </Stack>
                    </>
                )}

                {/* --- Photos : l'élément qui permet de trancher --- */}
                <Divider sx={{my: 2}}/>
                <Typography variant="subtitle2" sx={{mb: 1}}>
                    Photos de l'intervention ({photos.length})
                </Typography>
                {photos.length > 0 ? (
                    <Stack direction="row" spacing={1} sx={{overflowX: 'auto', pb: 1}}>
                        {photos.map((photo, i) => <PhotoThumb key={i} photo={photo}/>)}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Aucune photo disponible.
                    </Typography>
                )}

                {/* --- Actions --- */}
                <Divider sx={{my: 2}}/>
                <Typography variant="subtitle2" sx={{mb: 2}}>
                    {isLavageImpossible ? 'Que faire de ce lavage impossible ?' : 'Que faire de cet écart ?'}
                </Typography>

                {resolving ? (
                    <Stack alignItems="center" sx={{py: 3}}>
                        <CircularProgress/>
                    </Stack>
                ) : isLavageImpossible ? (
                    <Stack spacing={2}>
                        {/* 1. RAS : saisie et problème cohérents */}
                        <Box sx={{border: '1px solid #ddd', borderRadius: 2, p: 2}}>
                            <Typography variant="body2" sx={{mb: 1}}>
                                La raison et les photos sont cohérentes : rien à signaler.
                            </Typography>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<CheckCircleOutlineIcon/>}
                                sx={{backgroundColor: Constants.COLOR_THEME_GREEN, '&:hover': {backgroundColor: '#699a1c'}}}
                                onClick={() => onAcquitter(anomalie)}
                            >
                                RAS — acquitter
                            </Button>
                        </Box>

                        {/* 2. Corriger le problème rattaché (acquitte dans la foulée) */}
                        <Box sx={{border: '1px solid #ddd', borderRadius: 2, p: 2}}>
                            <Typography variant="body2" sx={{mb: 1}}>
                                Le problème rattaché est erroné : corriger la raison.
                            </Typography>
                            <Autocomplete
                                multiple
                                size="small"
                                options={problemesDisponibles}
                                value={selectedProblemes}
                                onChange={(e, value) => setSelectedProblemes(value)}
                                getOptionLabel={(p) => p.libelle ?? ''}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) =>
                                    <TextField {...params} label={`Problèmes critiques (${conteneur.conteneurType ?? 'toutes typologies'})`}/>
                                }
                                sx={{mb: 1}}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                startIcon={<EditIcon/>}
                                disabled={!problemesModifies}
                                onClick={() => onModifierProbleme(anomalie, selectedProblemes.map(problemeIri))}
                            >
                                Enregistrer le problème et valider
                            </Button>
                        </Box>

                        {/* 3. Requalifier en lavage réel */}
                        <Box sx={{border: '1px solid #ddd', borderRadius: 2, p: 2}}>
                            <Typography variant="body2" sx={{mb: 1}}>
                                Erreur de saisie : le lavage a bien eu lieu.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Nombre lavé"
                                    value={nombreLavesCorrige}
                                    onChange={(e) => setNombreLavesCorrige(e.target.value)}
                                    inputProps={{min: 0}}
                                    sx={{width: 130}}
                                />
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<LocalCarWashIcon/>}
                                    disabled={nombreLavesCorrige === '' || Number(nombreLavesCorrige) < 0}
                                    onClick={() => onCorriger(anomalie, {
                                        isAccessible: true,
                                        nombreLaves: Number(nombreLavesCorrige)
                                    })}
                                >
                                    Requalifier en lavage
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        {/* 1. Le terrain a raison */}
                        <Box sx={{border: '1px solid #ddd', borderRadius: 2, p: 2}}>
                            <Typography variant="body2" sx={{mb: 1}}>
                                Le terrain a raison (vérifié sur photos) : le patrimoine
                                passe de <b>{anomalie.quantitePatrimoine ?? '—'}</b> à <b>{anomalie.nombreLaves}</b>.
                            </Typography>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<CheckCircleOutlineIcon/>}
                                sx={{backgroundColor: Constants.COLOR_THEME_GREEN, '&:hover': {backgroundColor: '#699a1c'}}}
                                onClick={() => onPatrimoineMaj(anomalie)}
                            >
                                Mettre à jour le patrimoine
                            </Button>
                        </Box>

                        {/* 2. Erreur de saisie */}
                        <Box sx={{border: '1px solid #ddd', borderRadius: 2, p: 2}}>
                            <Typography variant="body2" sx={{mb: 1}}>
                                Erreur de saisie terrain : corriger le nombre lavé.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Nombre lavé"
                                    value={nombreLavesCorrige}
                                    onChange={(e) => setNombreLavesCorrige(e.target.value)}
                                    inputProps={{min: 0}}
                                    sx={{width: 130}}
                                />
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    startIcon={<EditIcon/>}
                                    disabled={nombreLavesCorrige === '' || Number(nombreLavesCorrige) < 0}
                                    onClick={() => onCorriger(anomalie, {nombreLaves: Number(nombreLavesCorrige)})}
                                >
                                    Corriger l'intervention
                                </Button>
                            </Stack>
                        </Box>

                        {/* 3. Ignorer */}
                        <Button
                            variant="text"
                            color="inherit"
                            startIcon={<VisibilityOffIcon/>}
                            onClick={() => onIgnorer(anomalie)}
                        >
                            Ignorer cet écart (justifié, ne rien modifier)
                        </Button>
                    </Stack>
                )}
            </Box>
        </Drawer>
    );
}

export default AnomalieDetailDrawer;
